import { useEffect, useState } from 'react';
import { api, errorText } from '../lib/api';
import { discardOp, flush, onSync, retryFailed, type SyncState } from '../lib/sync';
import { classNames, fmtDateTime } from '../lib/util';

export function AppSettings({ onSignedOut }: { onSignedOut: () => void }) {
  const [status, setStatus] = useState<any>(null);
  const [form, setForm] = useState<any>(null);
  const [jobs, setJobs] = useState<any[]>([]);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [sync, setSync] = useState<SyncState | null>(null);
  const [storage, setStorage] = useState<{ usage?: number; quota?: number; persisted?: boolean }>({});
  const load = async () => {
    try {
      const s = await api('/api/status');
      setStatus(s);
      setForm((f: any) => f ?? s.aiSettings);
      setJobs(await api('/api/jobs'));
    } catch (e) {
      setErr(errorText(e));
    }
  };
  useEffect(() => {
    void load();
    const off = onSync(setSync);
    (async () => {
      const est = await navigator.storage?.estimate?.().catch(() => undefined);
      const persisted = await navigator.storage?.persisted?.().catch(() => undefined);
      setStorage({ usage: est?.usage, quota: est?.quota, persisted });
    })();
    return off;
  }, []);
  const act = async (fn: () => Promise<unknown>, ok?: string) => {
    setErr('');
    setMsg('');
    try {
      await fn();
      if (ok) setMsg(ok);
      await load();
    } catch (e) {
      setErr(errorText(e));
    }
  };
  return (
    <div className="page narrow">
      <h1>App settings</h1>
      {err && <p className="error">{err}</p>}
      {msg && <p className="ok">{msg}</p>}

      <section className="card">
        <h2>AI service</h2>
        {status && (
          <p>
            Status: <strong>{status.ai.state}</strong>
            {status.ai.version ? ` · Ollama ${status.ai.version}` : ''} · chat model {status.ai.models.chat ? 'installed' : 'missing'} · embedding model {status.ai.models.embed ? 'installed' : 'missing'} · vision model{' '}
            {status.ai.models.vision ? 'installed' : 'not configured/missing'}
            {status.ai.error && <span className="muted small"> — {status.ai.error}</span>}
          </p>
        )}
        <div className="row wrap">
          <button className="btn" onClick={() => void act(() => api('/api/ai/pause', { body: { paused: !status?.ai.paused } }))}>
            {status?.ai.paused ? 'Resume AI processing' : 'Pause AI processing'}
          </button>
          <button className="btn" onClick={() => void act(() => api('/api/ai/check', { body: {} }), 'Checked')}>
            Check now
          </button>
        </div>
        {form && (
          <form
            className="form-grid"
            onSubmit={(e) => {
              e.preventDefault();
              void act(() => api('/api/settings/ai', { method: 'PUT', body: form }), 'AI settings saved');
            }}
          >
            <label className="span2">
              Ollama endpoint (server-side; never exposed to browsers)
              <input value={form.ollamaUrl} onChange={(e) => setForm({ ...form, ollamaUrl: e.target.value })} />
            </label>
            <label>
              Chat model
              <input value={form.chatModel} onChange={(e) => setForm({ ...form, chatModel: e.target.value })} />
            </label>
            <label>
              Embedding model
              <input value={form.embedModel} onChange={(e) => setForm({ ...form, embedModel: e.target.value })} />
            </label>
            <label>
              Vision/handwriting model (optional)
              <input value={form.visionModel} onChange={(e) => setForm({ ...form, visionModel: e.target.value })} placeholder="e.g. qwen3-vl:8b" />
            </label>
            <label>
              Context window (tokens)
              <input type="number" value={form.numCtx} min={2048} max={131072} step={1024} onChange={(e) => setForm({ ...form, numCtx: Number(e.target.value) })} />
            </label>
            <label>
              Concurrent AI jobs
              <input type="number" value={form.concurrency} min={1} max={4} onChange={(e) => setForm({ ...form, concurrency: Number(e.target.value) })} />
            </label>
            <label>
              Keep model loaded for
              <input value={form.keepAlive} onChange={(e) => setForm({ ...form, keepAlive: e.target.value })} placeholder="10m" />
            </label>
            <div className="span2">
              <button className="btn primary">Save AI settings</button>
            </div>
          </form>
        )}
      </section>

      <section className="card">
        <h2>OCR tools on the server</h2>
        {status && (
          <ul className="plain">
            <li>Tesseract: {status.tools.tesseract ?? <span className="warn">not installed</span>}</li>
            <li>pdftoppm (Poppler): {status.tools.pdftoppm ?? <span className="warn">not installed</span>}</li>
          </ul>
        )}
      </section>

      <section className="card">
        <h2>Background jobs</h2>
        {status && (
          <p className="muted small">
            {status.queue.queued} queued ({status.queue.waitingForAi} waiting for AI) · {status.queue.running} running · {status.queue.failed} failed
          </p>
        )}
        <ul className="jobs">
          {jobs.slice(0, 40).map((j) => (
            <li key={j.id} className={classNames(`job-${j.status}`)}>
              <span className="tag subtle">{j.type.replace('_', ' ')}</span> {j.status}
              {j.progress_text ? ` · ${j.progress_text}` : ''} <span className="muted small">{fmtDateTime(j.created_at)}</span>
              {j.last_error && <div className="small error">{j.last_error}</div>}
              {j.status === 'failed' && (
                <button className="link small" onClick={() => void act(() => api(`/api/jobs/${j.id}/retry`, { body: {} }))}>
                  Retry
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="card">
        <h2>This device</h2>
        {sync && (
          <>
            <p>
              {sync.online ? 'Online' : 'Offline'} · {sync.pending} change(s) waiting to sync · last sync {sync.lastSyncAt ? fmtDateTime(sync.lastSyncAt) : 'never'}
            </p>
            <button className="btn small" onClick={() => void flush()}>
              Sync now
            </button>
            {sync.failed.length > 0 && (
              <div className="notice">
                <p>These changes were rejected by the server and are kept here until you decide:</p>
                <ul>
                  {sync.failed.map((o) => (
                    <li key={o.seq}>
                      {o.type} — {o.payload.text ?? o.payload.title ?? ''} <span className="error small">{o.error}</span>{' '}
                      <button className="link small" onClick={() => void discardOp(o.seq!)}>
                        Discard
                      </button>
                    </li>
                  ))}
                </ul>
                <button className="btn small" onClick={() => void retryFailed()}>
                  Retry all
                </button>
              </div>
            )}
          </>
        )}
        <p className="muted small">
          Notes typed offline are stored in this browser until they reach the server. Browser storage can be cleared by the browser or by you, so it is not a backup. Storage used: {storage.usage ? `${(storage.usage / 1e6).toFixed(1)} MB` : 'unknown'}
          {storage.quota ? ` of ~${(storage.quota / 1e9).toFixed(1)} GB` : ''} · persistent storage {storage.persisted ? 'granted' : 'not granted'}.
        </p>
        {!storage.persisted && navigator.storage?.persist && (
          <button
            className="btn small"
            onClick={async () => {
              const ok = await navigator.storage.persist();
              setStorage((s) => ({ ...s, persisted: ok }));
            }}
          >
            Ask browser to keep offline data
          </button>
        )}
      </section>

      <section className="card">
        <h2>Account</h2>
        <PasswordForm onDone={() => setMsg('Password changed; other devices were signed out.')} />
        <button
          className="btn"
          onClick={async () => {
            await api('/api/auth/logout', { body: {} }).catch(() => undefined);
            onSignedOut();
          }}
        >
          Sign out
        </button>
      </section>
    </div>
  );
}

function PasswordForm({ onDone }: { onDone: () => void }) {
  const [f, setF] = useState({ current: '', next: '' });
  const [err, setErr] = useState('');
  return (
    <form
      className="row wrap"
      onSubmit={async (e) => {
        e.preventDefault();
        try {
          await api('/api/auth/password', { body: f });
          setF({ current: '', next: '' });
          setErr('');
          onDone();
        } catch (x) {
          setErr(errorText(x));
        }
      }}
    >
      <input type="password" value={f.current} onChange={(e) => setF({ ...f, current: e.target.value })} placeholder="Current password" aria-label="Current password" autoComplete="current-password" />
      <input type="password" value={f.next} onChange={(e) => setF({ ...f, next: e.target.value })} placeholder="New password (10+ chars)" aria-label="New password" autoComplete="new-password" minLength={10} />
      <button className="btn small">Change password</button>
      {err && <p className="error small">{err}</p>}
    </form>
  );
}
